import { Button } from '@hologram-dimension/button';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import useDevice from 'api/legacy/devices/useDevice';
import useSimDetails from 'api/sims/useSimDetails';
import useSimDeviceDetails from 'api/sims/useSimDeviceDetails';
import Table from 'components/Table/Table';
import { HeaderItem, TableRowItem } from 'components/Table/types';
import { useMemo, type FC } from 'react';
import formatMoney from 'utils/numbers/formatMoney';
import styles from './SimAddonsPanel.module.css';

interface SimAddonsPanelProps {
  hologramSimId?: HologramSimId;
  orgId?: OrgId;
  openPurchasePhoneNumberModal: () => void;
  openRemovePhoneNumberModal: () => void;
}

const SimAddonsPanel: FC<SimAddonsPanelProps> = ({
  hologramSimId,
  orgId,
  openPurchasePhoneNumberModal,
  openRemovePhoneNumberModal,
}) => {
  const {
    simDetails,
    error: simDetailsError,
    isLoading: isSimDetailsLoading,
    isValidating: isSimDetailsValidating,
  } = useSimDetails(hologramSimId);
  const {
    deviceDetails,
    error: simDeviceDetailsError,
    isLoading: isSimDeviceDetailsLoading,
    isValidating: isSimDeviceDetailsValidating,
  } = useSimDeviceDetails(hologramSimId);
  const {
    device: legacyDeviceDetails,
    error: deviceError,
    isLoading: isDeviceLoading,
    isValidating: isDeviceValidating,
  } = useDevice(deviceDetails.claimedDeviceId, orgId);
  const { phoneNumber } = simDetails;
  const { phonenumber_cost: phoneNumberCost } = legacyDeviceDetails;

  const error = simDetailsError || simDeviceDetailsError || deviceError;
  const isLoading = isSimDetailsLoading || isSimDeviceDetailsLoading || isDeviceLoading;
  const isValidating = isSimDetailsValidating || isSimDeviceDetailsValidating || isDeviceValidating;

  const panelContents = useMemo(() => {
    if (error) {
      return (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading the SIM add-ons"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          We will keep trying in the background or you can try to reload the page.
        </PanelNotification>
      );
    }

    if (isLoading) {
      return <PanelNotification>Loading Add-ons...</PanelNotification>;
    }

    const headers: HeaderItem[] = [
      {
        key: 'name',
        children: 'Name',
        width: 220,
      },
      {
        key: 'details',
        children: 'Details',
      },
      {
        key: 'cost',
        children: 'Cost',
      },
      {
        key: 'action',
        children: '',
      },
    ];

    const rows: TableRowItem[] = [
      {
        key: 'phone',
        cells: [
          {
            key: 'phone-name',
            children: 'Phone number',
            className: styles.columnName,
          },
          {
            key: 'phone-details',
            className: styles.column,
            children: phoneNumber || (
              <span className={styles.diminished}>No phone number added to this SIM</span>
            ),
          },
          {
            key: 'phone-cost',
            className: styles.column,
            children: phoneNumber ? formatMoney(parseFloat(phoneNumberCost ?? '0')) : '',
          },
          {
            key: 'phone-action',
            children: phoneNumber ? (
              <Button variant="delete" onClick={openRemovePhoneNumberModal}>
                Remove
              </Button>
            ) : (
              <Button variant="secondary" onClick={openPurchasePhoneNumberModal}>
                Purchase phone number
              </Button>
            ),
            className: styles.columnAction,
          },
        ],
      },
    ];

    return <Table headers={headers} rows={rows} />;
  }, [
    error,
    isLoading,
    openPurchasePhoneNumberModal,
    openRemovePhoneNumberModal,
    phoneNumber,
    phoneNumberCost,
  ]);

  return (
    <Panel header="Add-ons" noContentPadding isLoading={isLoading || isValidating}>
      {panelContents}
    </Panel>
  );
};
export default SimAddonsPanel;
